import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import debug from 'debug';
import { createRoot } from 'react-dom/client';
import { Provider as UrqlProvider } from 'urql';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { AuthProvider } from './contexts/auth.tsx';
import { urqlClient } from './graphql/client.ts';
import { ModalManagerProvider } from './hooks/useModalManager.tsx';
import './index.css';
import { router } from './routes/router.tsx';

// Expose debug controls on window for console access
declare global {
	interface Window {
		app: {
			debug: {
				enable: (namespace?: string) => void;
				disable: () => void;
				graphql: () => void;
				subscription: () => void;
				api: () => void;
				all: () => void;
			};
		};
	}
}

window.app = {
	debug: {
		// Enable specific namespace or all app:* by default
		enable: (namespace?: string) => debug.enable(namespace ?? 'app:*'),
		disable: () => debug.disable(),
		// Shortcuts for common debug scenarios
		graphql: () => debug.enable('app:graphql*,app:subscription*'),
		subscription: () => debug.enable('app:subscription*,app:graphql:ws*'),
		api: () => debug.enable('app:api*'),
		all: () => debug.enable('app:*'),
	},
};

// Start with debug disabled - use window.app.debug.enable() to turn on
// debug.enable('app:*');

// In standalone/PWA mode on iOS, links stay trapped in the webview.
// Intercept external link clicks and use '_system' target to open in Safari.
const isStandalone =
	window.matchMedia('(display-mode: standalone)').matches ||
	(navigator as unknown as { standalone?: boolean }).standalone === true;

if (isStandalone) {
	document.addEventListener('click', (e) => {
		const anchor = (e.target as HTMLElement).closest('a');
		if (!anchor) return;

		const href = anchor.getAttribute('href');
		if (!href) return;

		// Skip internal navigation (relative URLs, hash links, query params, javascript:, mailto:, tel:)
		if (/^[/?#]|^javascript:|^mailto:|^tel:/.test(href)) return;

		// Skip same-origin links (internal app navigation)
		try {
			if (new URL(href, window.location.origin).origin === window.location.origin) return;
		} catch { /* invalid URL, let it fall through */ }

		// External URL - '_system' is a non-standard but widely supported mobile target
		// that tells iOS to open in Safari instead of the in-app webview
		e.preventDefault();
		e.stopPropagation();
		window.open(href, '_system');
	});
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
		},
	},
});

createRoot(document.getElementById('root')!).render(
	<ErrorBoundary>
		<UrqlProvider value={urqlClient}>
			<QueryClientProvider client={queryClient}>
				<AuthProvider>
					<ModalManagerProvider>
						<RouterProvider router={router} />
					</ModalManagerProvider>
				</AuthProvider>
			</QueryClientProvider>
		</UrqlProvider>
	</ErrorBoundary>
);
